/**
 * AirCap Logic Page
 * 
 * Two views:
 *   (a) Project listing — cards showing AirCap analysis status per project
 *   (b) Detail — IATA ADRM terminal capacity analysis dashboard
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { projectsService, Project } from '../services/projects';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { aircapLogicService } from '../services/aircapLogicService';
import type {
    DDFSSchedule,
    ParameterSet,
    CapacityAnalysis,
    LevelOfService,
} from '../types/aircapLogic';
import { LOS_COLORS, LOS_LABELS, SUBSYSTEM_INFO } from '../types/aircapLogic';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import {
    Building2, Search, ListFilter, Calendar, Activity, ArrowRight,
    Plane, ArrowLeft, Upload, FileSpreadsheet, Dices, FlaskConical,
    Download, AlertTriangle, Settings, BookOpen, Construction,
    CheckCircle2, Clock, ChevronRight,
} from 'lucide-react';

// ==================== COMPONENT ====================

const AirCapLogicPage: React.FC = () => {
    const { theme } = useTheme();
    const { language } = useLanguage();
    const isDark = theme === 'dark';
    const [searchParams] = useSearchParams();

    // View state
    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
        searchParams.get('projectId')
    );

    // Project listing state
    const [projects, setProjects] = useState<Project[]>([]);
    const [loadingProjects, setLoadingProjects] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState<'date' | 'name'>('date');

    // Analysis detail state
    const [schedule, setSchedule] = useState<DDFSSchedule | null>(null);
    const [parameters, setParameters] = useState<ParameterSet | null>(null);
    const [analysis, setAnalysis] = useState<CapacityAnalysis | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [projectIcao, setProjectIcao] = useState<string>('XXXX');

    // Theme classes
    const textMain = 'text-[var(--text-primary)]';
    const textSub = 'text-[var(--text-secondary)]';
    const cardBg = isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-white border-[var(--border)] shadow-sm hover:shadow-md';

    // Load projects on mount
    useEffect(() => {
        loadProjects();
    }, []);

    const loadProjects = async () => {
        try {
            const data = await projectsService.getAll();
            setProjects(data);
        } catch (err) {
            console.error('Error loading projects:', err);
        } finally {
            setLoadingProjects(false);
        }
    };

    // When a project is selected, load its analysis data
    useEffect(() => {
        if (!selectedProjectId) return;

        loadDefaults();

        const loadProjectInfra = async () => {
            try {
                const project = await projectsService.getOne(selectedProjectId);

                // Store ICAO for analysis
                if (project?.icao_code) setProjectIcao(project.icao_code);

                // --- Infrastructure parameters ---
                if (project?.sme_parameters?.aircap_infra && parameters) {
                    const infra = project.sme_parameters.aircap_infra;
                    setParameters(prev => prev ? {
                        ...prev,
                        checkin_counters: infra.checkin_counters ?? prev.checkin_counters,
                        security_lanes: infra.security_lanes ?? prev.security_lanes,
                        passport_positions_dep: infra.passport_positions_dep ?? prev.passport_positions_dep,
                        passport_positions_arr: infra.passport_positions_arr ?? prev.passport_positions_arr,
                        gates_domestic: infra.gates_domestic ?? prev.gates_domestic,
                        gates_international: infra.gates_international ?? prev.gates_international,
                        gates_schengen: infra.gates_schengen ?? prev.gates_schengen,
                        gates_remote: infra.gates_remote ?? prev.gates_remote,
                        gates_jetbridge: infra.gates_jetbridge ?? prev.gates_jetbridge,
                    } : prev);
                }

                // --- Auto-load DDFS from IATA Busy Day extracted during DACAMS analysis ---
                const ar = project?.analysis_results;
                const hasFtsFlights = (ar?.fts_flights ?? []).length > 0;
                if (hasFtsFlights && selectedProjectId) {
                    try {
                        const registeredSchedule = await aircapLogicService.createDDFSFromProject(selectedProjectId);
                        setSchedule(registeredSchedule);
                    } catch (ftsErr: any) {
                        console.warn('AirCap: could not auto-load DDFS from project fts_flights:', ftsErr?.response?.data?.detail ?? ftsErr);
                    }
                }
            } catch (err) {
                console.error('Error loading project infra:', err);
            }
        };
        loadProjectInfra();
    }, [selectedProjectId]);

    const loadDefaults = async () => {
        try {
            const defaults = await aircapLogicService.getDefaults();
            setParameters(defaults);
        } catch (err) {
            console.error('Error loading defaults:', err);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        setLoading(true);
        setError(null);
        try {
            const uploadedSchedule = await aircapLogicService.uploadDDFS(file);
            setSchedule(uploadedSchedule);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error uploading file');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateSample = async () => {
        setLoading(true);
        setError(null);
        try {
            const sampleSchedule = await aircapLogicService.generateSampleDDFS();
            setSchedule(sampleSchedule);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error generating sample');
        } finally {
            setLoading(false);
        }
    };

    const handleRunAnalysis = async () => {
        if (!schedule) return;
        setLoading(true);
        setError(null);
        try {
            const result = await aircapLogicService.runAnalysis({
                schedule_id: schedule.id,
                airport_icao: projectIcao,
            });
            setAnalysis(result);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Error running analysis');
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadReport = async () => {
        if (!analysis) return;
        try {
            const blob = await aircapLogicService.downloadReport(analysis.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `aircap_report_${analysis.id}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading report:', err);
        }
    };

    const handleBackToProjects = () => {
        setSelectedProjectId(null);
        setSchedule(null);
        setAnalysis(null);
        setError(null);
    };

    const getLosColor = (los: LevelOfService): string => LOS_COLORS[los] || '#64748b';

    const chartData = analysis?.subsystem_results.map((result) => ({
        name: SUBSYSTEM_INFO[result.subsystem]?.name || result.subsystem,
        required: result.required_capacity,
        available: result.available_capacity,
        los: result.level_of_service,
    })) || [];

    const losSummary = analysis ? {
        optimum: analysis.optimum_count,
        subOptimum: analysis.subsystem_results.filter(r => r.level_of_service === 'SUB_OPTIMUM').length,
        underProvided: analysis.critical_count,
    } : { optimum: 0, subOptimum: 0, underProvided: 0 };

    // Filter & sort projects
    const processedProjects = projects
        .filter(p => {
            const search = searchTerm.toLowerCase();
            return (
                p.name.toLowerCase().includes(search) ||
                p.iata_code?.toLowerCase().includes(search) ||
                p.icao_code?.toLowerCase().includes(search)
            );
        })
        .sort((a, b) => {
            if (sortBy === 'date') return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
            return a.name.localeCompare(b.name);
        });

    // Derive AirCap status for a project
    const getAirCapStatus = (project: Project): { label: string; color: string; icon: React.ReactNode } => {
        const hasAirCapData = project.analysis_results?.aircap ||
            project.analysis_results?.apcas?.rac_capacity;
        if (hasAirCapData) {
            return {
                label: language === 'es' ? 'ANALIZADO' : 'ANALYZED',
                color: 'text-green-500',
                icon: <CheckCircle2 size={14} />,
            };
        }
        return {
            label: language === 'es' ? 'PENDIENTE' : 'PENDING',
            color: 'text-yellow-500',
            icon: <Clock size={14} />,
        };
    };

    // ===================== VIEW: PROJECT LISTING =====================
    if (!selectedProjectId) {
        return (
            <div className="p-8 font-sans relative min-h-full">
                <div className="max-w-7xl mx-auto">

                    {/* HEADER */}
                    <div className={`flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 border-b pb-8 ${isDark ? 'border-[#2a2a2a]' : 'border-gray-300'}`}>
                        <div>
                            <h1 className={`text-4xl font-extrabold flex items-center gap-3 ${textMain}`}>
                                <Building2 className="w-8 h-8 text-[#F26430]" />
                                AirCap
                            </h1>
                            <p className={`text-sm mt-2 tracking-wide font-medium ${textSub}`}>
                                {language === 'es'
                                    ? 'IATA ADRM — Capacidad de Terminal · Análisis Estático'
                                    : 'IATA ADRM — Terminal Building Capacity · Static Analysis'}
                            </p>
                        </div>
                    </div>

                    {/* SEARCH & SORT */}
                    <div className="flex flex-col md:flex-row gap-4 mb-8 max-w-4xl">
                        <div className={`flex-1 p-1 rounded-xl shadow-sm border flex items-center ${cardBg}`}>
                            <div className="pl-4 pr-2 text-[var(--text-secondary)]"><Search className="h-5 w-5" /></div>
                            <input
                                type="text"
                                placeholder={language === 'es' ? 'Buscar por nombre, código IATA o OACI...' : 'Search by name, IATA or ICAO code...'}
                                className={`w-full py-2 bg-transparent placeholder-gray-400 focus:outline-none text-sm ${textMain}`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <div className={`w-full md:w-64 p-1 rounded-xl shadow-sm border flex items-center px-4 ${cardBg}`}>
                            <ListFilter className="w-4 h-4 text-[#F26430] mr-2" />
                            <span className={`text-xs font-bold uppercase mr-2 ${textSub}`}>{language === 'es' ? 'ORDENAR' : 'SORT'}:</span>
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                                className={`bg-transparent outline-none text-sm font-bold cursor-pointer ${textMain} w-full`}
                            >
                                <option value="date" className="text-black">{language === 'es' ? 'Última Modif.' : 'Last Modified'}</option>
                                <option value="name" className="text-black">{language === 'es' ? 'Nombre (A-Z)' : 'Name (A-Z)'}</option>
                            </select>
                        </div>
                    </div>

                    {/* PROJECT GRID */}
                    {loadingProjects ? (
                        <div className="flex justify-center py-32"><div className="animate-spin rounded-full h-12 w-12 border-t-4 border-[#F26430]"></div></div>
                    ) : processedProjects.length === 0 ? (
                        <div className={`flex flex-col items-center justify-center py-24 rounded-2xl border-2 border-dashed ${isDark ? 'border-[var(--border)] bg-[var(--surface-raised)]0' : 'border-gray-300 bg-gray-50'}`}>
                            <Building2 className="h-12 w-12 text-[var(--text-secondary)] mb-4" />
                            <p className={textSub}>{language === 'es' ? 'No hay proyectos. Crea uno desde el HUB DACAMS.' : 'No projects. Create one from the DACAMS HUB.'}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
                            {processedProjects.map((project) => {
                                const status = getAirCapStatus(project);
                                return (
                                    <div
                                        key={project.id}
                                        onClick={() => setSelectedProjectId(project.id)}
                                        className={`group rounded-xl border shadow-lg hover:shadow-2xl transition-all cursor-pointer overflow-hidden relative flex flex-col ${cardBg} hover:border-[#F26430]/50`}
                                    >
                                        <div className={`h-1 w-full ${status.color === 'text-green-500' ? 'bg-green-500' : 'bg-yellow-500'}`} />

                                        <div className="p-6 flex-1">
                                            <div className="flex items-center gap-4 mb-4">
                                                <div className={`h-12 w-12 rounded-lg flex items-center justify-center font-mono font-bold text-lg shadow-inner ${isDark ? 'bg-[var(--surface-raised)] text-[var(--text-primary)] border border-[var(--border)]' : 'bg-gray-100 text-[#1C1C1C] border border-[var(--border)]'}`}>
                                                    {project.iata_code || <Plane size={20} />}
                                                </div>
                                                <div>
                                                    <h3 className={`font-bold text-lg line-clamp-1 ${textMain}`}>{project.name}</h3>
                                                    <p className="text-xs font-mono text-[var(--text-tertiary)] bg-opacity-10 bg-gray-500 px-2 py-0.5 rounded inline-block mt-1">
                                                        {project.icao_code || '----'}
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="space-y-2 mt-4 pt-4 border-t border-gray-500/20">
                                                <div className="flex justify-between text-xs">
                                                    <span className={`flex items-center gap-2 ${textSub}`}><Calendar size={14} /> {language === 'es' ? 'Creado' : 'Created'}</span>
                                                    <span className={`font-mono ${textMain}`}>{project.created_at ? new Date(project.created_at).toLocaleDateString() : '-'}</span>
                                                </div>
                                                <div className="flex justify-between text-xs">
                                                    <span className={`flex items-center gap-2 ${textSub}`}><Activity size={14} /> AirCap</span>
                                                    <span className={`font-bold flex items-center gap-1 ${status.color}`}>
                                                        {status.icon} {status.label}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`px-6 py-3 border-t flex justify-between items-center group-hover:bg-blue-500/5 transition-colors ${isDark ? 'bg-[var(--surface)] border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
                                            <span className={`text-[10px] font-bold uppercase tracking-widest ${textSub}`}>{language === 'es' ? 'Abrir AirCap' : 'Open AirCap'}</span>
                                            <ArrowRight className="h-4 w-4 text-[#F26430] transform group-hover:translate-x-1 transition-transform" />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // ===================== VIEW: ANALYSIS DETAIL =====================
    const selectedProject = projects.find(p => p.id === selectedProjectId);

    return (
        <div className={`p-6 font-sans min-h-full ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            <div className="max-w-[1600px] mx-auto">

                {/* Header with Back button */}
                <div className={`flex items-center justify-between mb-6 border-b pb-6 ${isDark ? 'border-[#2a2a2a]' : 'border-gray-300'}`}>
                    <div className="flex items-center gap-4">
                        <button
                            onClick={handleBackToProjects}
                            className={`p-2 rounded-lg border transition-colors ${isDark ? 'border-[var(--border)] hover:bg-[var(--surface-raised)]' : 'border-gray-300 hover:bg-gray-100'}`}
                        >
                            <ArrowLeft className="w-5 h-5 text-[#F26430]" />
                        </button>
                        <div>
                            <h1 className={`text-2xl font-bold flex items-center gap-3 ${textMain}`}>
                                <Building2 className="w-6 h-6 text-[#F26430]" />
                                AirCap Logic
                                {selectedProject && (
                                    <span className={`text-base font-normal ${textSub}`}>
                                        — {selectedProject.iata_code ? `[${selectedProject.iata_code}] ` : ''}{selectedProject.name}
                                    </span>
                                )}
                            </h1>
                            <p className={`text-sm mt-1 ${textSub}`}>
                                {language === 'es'
                                    ? 'IATA ADRM — Planificación de Capacidad de Terminal · Análisis Estático'
                                    : 'IATA ADRM Terminal Capacity Planning · Static Analysis Mode'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Error display */}
                {error && (
                    <div className={`mb-5 p-4 rounded-xl border flex items-center gap-3 ${isDark ? 'bg-red-900/30 border-red-500/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                        <AlertTriangle className="w-5 h-5 flex-shrink-0" /> {error}
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                    {/* DDFS Upload Card */}
                    <div className={`p-5 rounded-xl border ${isDark ? 'bg-[#111827]/90 border-[var(--border)]' : 'bg-white border-[var(--border)]'}`}>
                        <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${'text-[var(--text-primary)]'}`}>
                            <FileSpreadsheet className="w-5 h-5 text-[#F26430]" />
                            Design Day Flight Schedule
                        </h2>

                        {!schedule ? (
                            <div>
                                <label htmlFor="ddfs-upload" className={`block border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${isDark ? 'border-[var(--border)] hover:border-[#F26430]' : 'border-gray-300 hover:border-[#F26430]'}`}>
                                    <input
                                        type="file"
                                        id="ddfs-upload"
                                        accept=".csv"
                                        onChange={handleFileUpload}
                                        className="hidden"
                                    />
                                    <Upload className={`w-8 h-8 mx-auto mb-3 ${textSub}`} />
                                    <p className={`${textSub} mb-2 text-sm`}>
                                        {language === 'es' ? 'Arrastra CSV o haz clic para subir' : 'Drag & drop CSV or click to upload'}
                                    </p>
                                    <p className={`text-xs ${isDark ? 'text-gray-600' : 'text-[var(--text-secondary)]'}`}>
                                        Format: flight_id, time, direction, aircraft_type, seats
                                    </p>
                                </label>

                                <div className="mt-4 text-center">
                                    <button
                                        onClick={handleGenerateSample}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'bg-[var(--surface-inset)] hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                                        disabled={loading}
                                    >
                                        <span className="flex items-center gap-2 justify-center">
                                            <Dices className="w-4 h-4" />
                                            {loading ? (language === 'es' ? 'Cargando...' : 'Loading...') : (language === 'es' ? 'Generar DDFS de Ejemplo' : 'Generate Sample DDFS')}
                                        </span>
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <StatBox label={language === 'es' ? 'Vuelos Totales' : 'Total Flights'} value={schedule.total_flights} isDark={isDark} />
                                    <StatBox label="Peak Hour PAX" value={schedule.php_total} isDark={isDark} />
                                    <StatBox label="PHP Departures" value={schedule.php_departures} color="#22c55e" isDark={isDark} />
                                    <StatBox label="PHP Arrivals" value={schedule.php_arrivals} color="#3b82f6" isDark={isDark} />
                                </div>

                                <button
                                    onClick={handleRunAnalysis}
                                    className="w-full px-4 py-3 rounded-lg bg-gradient-to-r from-[#F26430] to-[#e04f1a] text-white font-bold text-sm shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    disabled={loading}
                                >
                                    <FlaskConical className="w-4 h-4" />
                                    {loading ? (language === 'es' ? 'Analizando...' : 'Analyzing...') : (language === 'es' ? 'Ejecutar Análisis' : 'Run Capacity Analysis')}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Parameters Card */}
                    <div className={`p-5 rounded-xl border ${isDark ? 'bg-[#111827]/90 border-[var(--border)]' : 'bg-white border-[var(--border)]'}`}>
                        <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${'text-[var(--text-primary)]'}`}>
                            <Settings className="w-5 h-5 text-[#F26430]" />
                            {language === 'es' ? 'Parámetros IATA' : 'IATA Parameters'}
                        </h2>

                        {parameters && (
                            <div className="space-y-3">
                                <ParamRow label="Load Factor" value={`${(parameters.load_factor * 100).toFixed(0)}%`} isDark={isDark} />
                                <ParamRow label="Transfer PAX" value={`${(parameters.transfer_pax_pct * 100).toFixed(0)}%`} isDark={isDark} />
                                <ParamRow label="Check-in Process Time" value={`${parameters.checkin_process_time_min} min`} isDark={isDark} />
                                <ParamRow label="Security Capacity" value={`${parameters.security_load_factor} pax/hr`} isDark={isDark} />
                                <ParamRow label="Self-Service Usage" value={`${(parameters.selfservice_pct * 100).toFixed(0)}%`} isDark={isDark} />
                                <ParamRow label="Companion Ratio" value={`${parameters.companion_ratio}`} isDark={isDark} />

                                <p className={`text-[11px] mt-3 flex items-center gap-1.5 ${isDark ? 'text-gray-600' : 'text-[var(--text-secondary)]'}`}>
                                    <BookOpen className="w-3 h-3" /> Source: IATA ADRM 12th Edition
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Infrastructure Inventory Card */}
                <div className={`p-5 rounded-xl border mb-5 ${isDark ? 'bg-[#111827]/90 border-[var(--border)]' : 'bg-white border-[var(--border)]'}`}>
                    <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${'text-[var(--text-primary)]'}`}>
                        <Construction className="w-5 h-5 text-[#F26430]" />
                        Infrastructure Inventory
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatBox label="Check-in Counters" value={parameters?.checkin_counters ?? '—'} isDark={isDark} />
                        <StatBox label="Security Lanes" value={parameters?.security_lanes ?? '—'} isDark={isDark} />
                        <StatBox label="Passport Pos. (Dep)" value={parameters?.passport_positions_dep ?? '—'} isDark={isDark} />
                        <StatBox label="Passport Pos. (Arr)" value={parameters?.passport_positions_arr ?? '—'} isDark={isDark} />
                    </div>

                    <div className="mt-4">
                        <h3 className={`text-sm font-bold mb-3 ${textSub}`}>Gate Configuration</h3>
                        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                            {[
                                { label: 'Domestic', value: parameters?.gates_domestic ?? 0 },
                                { label: 'International', value: parameters?.gates_international ?? 0 },
                                { label: 'Schengen', value: parameters?.gates_schengen ?? 0 },
                                { label: 'Remote (Bus)', value: parameters?.gates_remote ?? 0 },
                                { label: 'Jet Bridge', value: parameters?.gates_jetbridge ?? 0 },
                            ].map(item => (
                                <div key={item.label} className={`p-2 rounded-lg text-center border ${isDark ? 'bg-[#1e293b] border-[var(--border)]' : 'bg-gray-50 border-[var(--border)]'}`}>
                                    <div className={`text-lg font-bold ${textMain}`}>{item.value}</div>
                                    <div className={`text-[10px] ${textSub}`}>{item.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Results Section */}
                {analysis && (
                    <>
                        {/* Summary Stats */}
                        <div className={`p-5 rounded-xl border mb-5 ${isDark ? 'bg-[#111827]/90 border-[var(--border)]' : 'bg-white border-[var(--border)]'}`}>
                            <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${'text-[var(--text-primary)]'}`}>
                                <Activity className="w-5 h-5 text-[#F26430]" />
                                {language === 'es' ? 'Resultados del Análisis' : 'Analysis Results'}
                                <span className="ml-auto px-3 py-1 rounded-full text-xs font-bold text-white" style={{ backgroundColor: getLosColor(analysis.overall_los) }}>
                                    {LOS_LABELS[analysis.overall_los]}
                                </span>
                            </h2>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                                <StatBox label={language === 'es' ? 'Subsistemas' : 'Subsystems'} value={analysis.total_subsystems} isDark={isDark} />
                                <StatBox label="Optimum" value={losSummary.optimum} color={LOS_COLORS.OPTIMUM} isDark={isDark} borderColor={LOS_COLORS.OPTIMUM} />
                                <StatBox label="Sub-Optimum" value={losSummary.subOptimum} color={LOS_COLORS.SUB_OPTIMUM} isDark={isDark} borderColor={LOS_COLORS.SUB_OPTIMUM} />
                                <StatBox label="Under-Provided" value={losSummary.underProvided} color={LOS_COLORS.UNDER_PROVIDED} isDark={isDark} borderColor={LOS_COLORS.UNDER_PROVIDED} />
                            </div>

                            <button
                                onClick={handleDownloadReport}
                                className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${isDark ? 'bg-[var(--surface-inset)] hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                            >
                                <Download className="w-4 h-4" />
                                {language === 'es' ? 'Descargar Informe PDF' : 'Download PDF Report'}
                            </button>
                        </div>

                        {/* Traffic Light Grid */}
                        <div className={`p-5 rounded-xl border mb-5 ${isDark ? 'bg-[#111827]/90 border-[var(--border)]' : 'bg-white border-[var(--border)]'}`}>
                            <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${'text-[var(--text-primary)]'}`}>
                                <ChevronRight className="w-5 h-5 text-[#F26430]" />
                                {language === 'es' ? 'Estado de Subsistemas' : 'Subsystem Status'}
                            </h2>

                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                                {analysis.subsystem_results.map((result) => (
                                    <div
                                        key={result.id}
                                        className="rounded-lg p-3 text-center transition-transform hover:scale-105"
                                        style={{
                                            backgroundColor: getLosColor(result.level_of_service) + '20',
                                            border: `2px solid ${getLosColor(result.level_of_service)}`,
                                        }}
                                    >
                                        <div className="w-4 h-4 rounded-full mx-auto mb-2" style={{ backgroundColor: getLosColor(result.level_of_service) }} />
                                        <div className={`text-[11px] font-bold ${textMain}`}>
                                            {SUBSYSTEM_INFO[result.subsystem]?.name || result.subsystem}
                                        </div>
                                        <div className={`text-[10px] mt-1 ${textSub}`}>
                                            {result.required_capacity.toFixed(1)} / {result.available_capacity || '—'} {result.unit}
                                        </div>
                                        <div className="text-[10px] mt-0.5" style={{ color: getLosColor(result.level_of_service) }}>
                                            {result.utilization_pct.toFixed(0)}% utilization
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Demand vs Capacity Chart */}
                        <div className={`p-5 rounded-xl border ${isDark ? 'bg-[#111827]/90 border-[var(--border)]' : 'bg-white border-[var(--border)]'}`}>
                            <h2 className={`text-base font-bold mb-4 flex items-center gap-2 ${'text-[var(--text-primary)]'}`}>
                                <Activity className="w-5 h-5 text-[#F26430]" />
                                {language === 'es' ? 'Demanda vs Capacidad' : 'Demand vs Capacity'}
                            </h2>

                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={chartData}
                                    layout="vertical"
                                    margin={{ top: 20, right: 30, left: 150, bottom: 5 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                                    <XAxis type="number" stroke={isDark ? '#888' : '#6b7280'} />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        stroke={isDark ? '#888' : '#6b7280'}
                                        tick={{ fontSize: 11 }}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1C1C1C' : '#ffffff',
                                            border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`,
                                            borderRadius: '8px',
                                        }}
                                    />
                                    <Legend />
                                    <Bar dataKey="required" name={language === 'es' ? 'Requerido' : 'Required'} fill="#3b82f6" />
                                    <Bar dataKey="available" name={language === 'es' ? 'Disponible' : 'Available'} fill="#22c55e" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

// ==================== Sub-Components ====================

const StatBox: React.FC<{ label: string; value: any; color?: string; isDark: boolean; borderColor?: string }> = ({ label, value, color, isDark, borderColor }) => (
    <div className={`p-3 rounded-lg text-center ${'bg-[var(--surface-raised)]'}`}
        style={borderColor ? { borderLeft: `3px solid ${borderColor}` } : undefined}
    >
        <div className="text-2xl font-bold" style={color ? { color } : { color: isDark ? '#fff' : '#111' }}>{value}</div>
        <div className={`text-[11px] mt-1 ${'text-[var(--text-secondary)]'}`}>{label}</div>
    </div>
);

const ParamRow: React.FC<{ label: string; value: string; isDark: boolean }> = ({ label, value, isDark }) => (
    <div className="flex justify-between items-center">
        <span className={`text-sm ${'text-[var(--text-secondary)]'}`}>{label}</span>
        <span className={`text-sm font-medium ${'text-[var(--text-primary)]'}`}>{value}</span>
    </div>
);

export default AirCapLogicPage;
